import React, { useMemo } from "react";

import { cloneDeep } from "lodash";

import videoTooltipIcon from "@assets/video-tooltip.png";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import { useTableState } from "@features/sales-v3/hooks/use-table-state";
import { type SalesTableVisualRow } from "@features/sales-v3/types/table";
import { useFeatureFlags } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { formatting } from "@north-beam/nb-common";

const REV = "rev";
const TXNS = "txns";
const tooltipEnabledTikTokColumns = [REV, TXNS];
const canShowTikTokShopColumnTooltip = (columnDef: ColumnDef) => {
  const isRevOrTxnColumn = tooltipEnabledTikTokColumns.includes(
    columnDef.metric.composition.metric,
  );
  const isLtvCashColumn =
    columnDef.metric.metadata.isLtv &&
    columnDef.metric.composition.details?.accountingMode === "cash";
  const isNotLtvRevColumn = !columnDef.metric.metadata.isLtv;
  const isNotFirstOrReturningColumn = !["First Time", "Returning"].includes(
    columnDef.metric.metadata.type || "",
  );

  return (
    isRevOrTxnColumn &&
    (isLtvCashColumn || isNotLtvRevColumn) &&
    isNotFirstOrReturningColumn
  );
};

const METRIC_MAP: Record<string, string | undefined> = {
  [REV]: "revenue",
  [TXNS]: "transactions",
};

type Props = {
  columnDef: ColumnDef;
};

export const TikTokShopsColumnTooltip = ({
  children,
  columnDef,
  row,
}: Props & {
  children: React.ReactNode;
  row: SalesTableVisualRow | undefined;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const metricName = METRIC_MAP[columnDef.metric.composition.metric];
  const iconKey = row?.rowMetadata?.platform || row?.rowMetadata?.naming?.name;

  const canShowTikTokShopColumns = useMemo(() => {
    const enableShopMetricColumns = hasFeatureFlag("enableShopMetricColumns");
    const enableShopMetricTooltip = hasFeatureFlag("enableShopMetricTooltip");

    if (
      enableShopMetricColumns &&
      enableShopMetricTooltip &&
      canShowTikTokShopColumnTooltip(columnDef) &&
      metricName &&
      iconKey &&
      row
    ) {
      return true;
    } else {
      return false;
    }
  }, [hasFeatureFlag, columnDef, iconKey, metricName, row]);

  if (canShowTikTokShopColumns && iconKey && row) {
    return (
      <div className="flex-center h-full">
        {children}
        <GenericTooltip
          placement="bottom"
          className="mr-2"
          content={
            <TikTokShopsColumnTooltipContent
              metricName={metricName}
              iconKey={iconKey}
              row={row}
              columnDef={columnDef}
            />
          }
        ></GenericTooltip>
      </div>
    );
  }

  return <>{children}</>;
};

const TikTokShopsColumnTooltipContent = ({
  metricName,
  columnDef,
  iconKey,
  row,
}: Props & {
  metricName: string | undefined;
  iconKey: string;
  row: SalesTableVisualRow;
}) => {
  const { user } = useUser();
  const { columnConfigs, setColumnConfigs } = useTableState();
  const { designElementManager } = useDesignElementManager();
  const icon = designElementManager.getDesignElement(iconKey).getComponent({
    className: "mr-1.5",
    size: "24px",
  });

  const addColumns = () => {
    const shops = ["WebShops", "MetaShops", "TikTokShops"];
    const columnConfigsCopy = cloneDeep(columnConfigs).filter(
      (config) =>
        !shops.map((shop) => `${metricName}${shop}`).includes(config.id),
    );

    const columnIndex = columnConfigsCopy.findIndex(
      ({ id }) => id === columnDef.id,
    );
    shops.forEach((shop, index) =>
      columnConfigsCopy.splice(columnIndex + index + 1, 0, {
        id: `${metricName}${shop}`,
        showMiniGraph: false,
        width: 200,
      }),
    );
    setColumnConfigs(columnConfigsCopy);
  };

  const renderPercent = (
    platform: "WebShops" | "TikTokShops" | "MetaShops",
  ) => (
    <div className="font-mono text-right text-cosmic">
      {formatting.formatPercentage(
        row?.export?.[`${metricName}${platform}`] / row?.export?.[columnDef.id],
        {},
      )}
    </div>
  );

  const renderValue = (platform: "WebShops" | "TikTokShops" | "MetaShops") => {
    const value = row?.export?.[`${metricName}${platform}`];
    const formattedValue =
      metricName === "revenue"
        ? formatting.formatCurrency(value, {
            code: user.currencies.currentCurrency,
          })
        : formatting.formatDecimal(value, {});

    return <div className="font-mono text-right">{formattedValue}</div>;
  };

  const renderEllipsis = () => (
    <div className="text-[#E6E6E9] h-2 mb-4 leading-4">
      {Array.from({ length: 100 }, () => ".").map((dot) => dot)}
    </div>
  );

  return (
    <div className="text-sm max-w-xs p-1">
      <div className="text-xs mb-3 flex items-center">
        {row.key === "[]" ? (
          "TOTAL"
        ) : (
          <>
            {icon}
            <div className="truncate">{row.rowMetadata.naming.name}</div>
          </>
        )}
      </div>
      <div className="grid grid-cols-[50fr_auto_auto] gap-3">
        <div className="font-semibold col-span-2 overflow-hidden whitespace-nowrap w-full flex items-center">
          <div className="mr-3">{columnDef.title}</div>
          {renderEllipsis()}
        </div>
        <div className="font-mono font-medium text-right">
          {row?.display?.[columnDef.id]}
        </div>
        {/* WEB SHOPS */}
        <div className="overflow-hidden whitespace-nowrap w-full flex items-center">
          <div className="mr-3 flex items-center">
            {designElementManager.getDesignElement("shopify").getComponent({
              className: "mr-1.5",
              size: "24px",
            })}
            Web
          </div>
          {renderEllipsis()}
        </div>
        {renderPercent("WebShops")}
        {renderValue("WebShops")}
        {/* META SHOPS */}
        <div className="overflow-hidden whitespace-nowrap w-full flex items-center">
          <div className="mr-3 flex items-center">
            {designElementManager.getDesignElement("facebook").getComponent({
              className: "mr-1.5",
              size: "24px",
            })}
            Meta Shops
          </div>
          {renderEllipsis()}
        </div>
        {renderPercent("MetaShops")}
        {renderValue("MetaShops")}
        {/* TIKTOK SHOPS */}
        <div className="overflow-hidden whitespace-nowrap w-full flex items-center">
          <div className="mr-3 flex items-center">
            {designElementManager.getDesignElement("tiktok").getComponent({
              className: "mr-1.5",
              size: "24px",
            })}
            TikTok Shops
          </div>
          {renderEllipsis()}
        </div>
        {renderPercent("TikTokShops")}
        {renderValue("TikTokShops")}
      </div>
      <div className="mt-5 text-cosmic flex items-center justify-between">
        <div className="flex items-center hover:text-cosmic+2">
          <img
            className="h-4 mr-1"
            src={videoTooltipIcon}
            alt="videoTooltipIcon"
          />
          <a
            className="underline text-xxs"
            href="https://docs.northbeam.io/docs/tracking-for-shops"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn how we calculate
          </a>
        </div>
        <div className="flex items-center hover:text-cosmic+2 cursor-pointer">
          <i className="fa-regular fa-columns-3 mr-1 text-xs" />
          <div className="underline text-xxs" onClick={addColumns}>
            Add as columns
          </div>
        </div>
      </div>
    </div>
  );
};
